import {
  twoFactorDisable,
  twoFactorEnrol,
  twoFactorRegenerateRecoveryCodes,
  twoFactorVerify,
} from '@/api/generated/auth/auth';
import {
  billingCancel,
  billingInvoicePdf,
  billingListInvoices,
  billingListPlans,
  billingPayInvoice,
  billingQuote,
  billingResume,
  billingSubscribe,
  billingSubscription,
  billingSubscriptionWalletBalance,
  billingSubscriptionWalletTopUp,
  billingSubscriptionWalletTransactions,
  billingTopUp,
  billingWalletBalance,
  billingWalletTransactions,
  offersClaim,
  offersDismiss,
  offersEnterCode,
  offersList,
  offersMarkSeen,
} from '@/api/generated/billing/billing';
import {
  clinicsCompleteOnboarding,
  clinicsCurrent,
  clinicsOnboardingState,
  clinicsSetDirectory,
  clinicsSubmitVerification,
  clinicsUpdate,
  clinicsVerificationState,
  clinicsVerificationUploadUrl,
} from '@/api/generated/clinic/clinic';
import {
  starterKitsApply,
  starterKitsList,
  starterKitsPreview,
} from '@/api/generated/clinics/clinics';
import { uploadFailureFa } from '@/lib/upload-failure';
import { directoryPreviewStatus } from '@/api/generated/directory-clinic-panel/directory-clinic-panel';
import {
  patientImportCommit,
  patientImportFormat,
  patientImportPreview,
} from '@/api/generated/patients/patients';
import { roomsCreate, roomsList, roomsRemove, roomsUpdate } from '@/api/generated/rooms/rooms';
import { parsePublicPricing, type PublicPricing } from '@bookora/shared';
import type {
  ApplyStarterKitDtoKey,
  ClinicOfferClaimResponse,
  ClinicOfferResponse,
  ClinicOffersResponse,
  BillingQuoteTermMonths,
  BillingRaasStateResponse,
  RaasChargeRowResponse,
  RaasRateViewResponse,
  RaasSummaryResponseDto,
  RefundEligibilityResponse,
  TreatedPatientsMonthResponse,
  TreatedPatientsResponse,
  SubscribeDtoTermMonths,
  ClinicBillingStateResponse,
  PlanChangeQuoteResponse,
  ResultRateResponse,
  SubscriptionResponse,
  ClinicResponse,
  ClinicSocialsDto,
  ClinicVerificationStateResponse,
  CreateRoomDto,
  InvoiceResponse,
  PatientImportFormatResponse,
  PatientImportResultResponse,
  StarterKitApplyResultResponse,
  StarterKitPreviewResponse,
  StarterKitSummaryResponse,
  SubmitVerificationDto,
  UpdateClinicDto,
  UpdateRoomDto,
  VerificationUploadUrlDto,
  WalletTransactionResponse,
} from '@/api/generated/model';
import { apiGet, apiPost, apiPut, http } from '@/api/mutator';
import { requestBlob } from '@/lib/download';

/**
 * The settings section's wire layer.
 *
 * Two corrections live here and nowhere else:
 *
 *  1. **The `{ data }` envelope.** The mutator already unwraps it, so
 *     `billingListInvoices` — which orval typed as `{ data: … }` — really
 *     resolves to the inner paged result.
 *  2. **`Clinic.socials`.** It is a Postgres JSON column, so Swagger describes
 *     it as an untyped object and orval emits `{ [key: string]: unknown }`.
 *     Its real shape is `ClinicSocialsDto`, which the update DTO already
 *     states; {@link Clinic} says so on the read side too.
 *
 * **Money is integer rials in every field below**, exactly as stored. Nothing
 * here converts; `<Money>` and `MoneyInput` do that at the edge.
 */

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

export type Clinic = Omit<ClinicResponse, 'socials'> & {
  socials?: ClinicSocialsDto | null;
};

export type ClinicUpdate = UpdateClinicDto;

export type Invoice = InvoiceResponse;
export type WalletTransaction = WalletTransactionResponse;
export type StarterKitSummary = StarterKitSummaryResponse;
export type StarterKitPreview = StarterKitPreviewResponse;
export type StarterKitApplyResult = StarterKitApplyResultResponse;
export type PatientImportFormat = PatientImportFormatResponse;
export type PatientImportResult = PatientImportResultResponse;

/* -------------------------------------------------------------------------- */
/* Clinic                                                                     */
/* -------------------------------------------------------------------------- */

export function fetchClinic(signal?: AbortSignal): Promise<Clinic> {
  return clinicsCurrent(signal);
}

export function fetchOnboarding(signal?: AbortSignal) {
  return clinicsOnboardingState(signal);
}

export function fetchDirectoryPreview(signal?: AbortSignal) {
  return directoryPreviewStatus(signal);
}

export const clinicApi = {
  update: (dto: ClinicUpdate) => clinicsUpdate(dto),
  setDirectoryVisible: (directoryVisible: boolean) => clinicsSetDirectory({ directoryVisible }),
  completeOnboarding: () => clinicsCompleteOnboarding(),
};

/* -------------------------------------------------------------------------- */
/* Licence verification                                                       */
/* -------------------------------------------------------------------------- */

export function fetchVerificationState(
  signal?: AbortSignal,
): Promise<ClinicVerificationStateResponse> {
  return clinicsVerificationState(signal);
}

/**
 * Uploads one document straight to the private bucket, then returns the storage
 * key `SubmitVerificationDto.documents[].fileKey` expects.
 *
 * Two steps, both required by the server: `POST …/verification/upload-url` mints
 * a short-lived pre-signed `PUT` (the file never touches our API), then the file
 * bytes go to that URL directly. Plain `fetch`, not the api client — the signed
 * URL is on the storage host and must not carry our bearer token.
 */
export async function uploadVerificationDocument(input: {
  kind: VerificationUploadUrlDto['kind'];
  file: File;
}): Promise<{
  kind: VerificationUploadUrlDto['kind'];
  fileKey: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
}> {
  const extension = (input.file.name.split('.').pop() ?? '').toLowerCase();
  const ticket = await clinicsVerificationUploadUrl({
    kind: input.kind,
    extension: extension as VerificationUploadUrlDto['extension'],
    sizeBytes: input.file.size,
  });

  const put = await fetch(ticket.url, {
    method: 'PUT',
    body: input.file,
    headers: { 'Content-Type': ticket.contentType },
  });
  if (!put.ok) {
    throw new Error(uploadFailureFa(input.file.name, put.status));
  }

  return {
    kind: input.kind,
    fileKey: ticket.key,
    originalName: input.file.name,
    mimeType: input.file.type || ticket.contentType,
    sizeBytes: input.file.size,
  };
}

export const verificationApi = {
  submit: (dto: SubmitVerificationDto) => clinicsSubmitVerification(dto),
};

/* -------------------------------------------------------------------------- */
/* Rooms                                                                      */
/* -------------------------------------------------------------------------- */

export function fetchRooms(signal?: AbortSignal) {
  return roomsList({}, signal);
}

export const roomApi = {
  create: (dto: CreateRoomDto) => roomsCreate(dto),
  update: (id: string, dto: UpdateRoomDto) => roomsUpdate(id, dto),
  /**
   * `DELETE /rooms/:id` only succeeds for a room nothing references; otherwise
   * the API refuses, which is why the panel offers «غیرفعال‌سازی» first.
   */
  remove: (id: string) => roomsRemove(id),
};

/* -------------------------------------------------------------------------- */
/* Billing                                                                    */
/* -------------------------------------------------------------------------- */

/*
 * ── The pricing build's billing contract (CONTRACTS-PRICING.md §4.4–§4.5) ──
 *
 * Everything the subscription and «نتایج و صورت‌حساب» tabs read beyond the
 * pre-build DTOs. Since checkpoint C1 these are the GENERATED models under the
 * names the screens import — every screen imports them from this file, so the
 * contract is named once. Money is integer rials everywhere, exactly as on the wire.
 */

export type BillingModel = 'FLAT' | 'RAAS';

/** `ClinicBillingStateResponse.raas` (§4.4) — the banner's and the results tab's alert slice. */
export type RaasBillingStateSlice = BillingRaasStateResponse;

/** A RaaS rate as a plan row and a subscription snapshot carry it (`ResultRateResponse`). */
export type RaasRateRow = ResultRateResponse;

/** `SubscriptionResponse` (carries the RaaS rate snapshot as `resultRate`). */
export type Subscription = SubscriptionResponse;

/** `ClinicBillingStateResponse` (§4.5). */
export type BillingState = ClinicBillingStateResponse;

/** One enforced feature a plan change would take away (§4.5 quote additions). */
export type FeatureLost = PlanChangeQuoteResponse['featuresLost'][number];

/** `PlanChangeQuoteResponse` (§4.5). */
export type PlanChangeQuote = PlanChangeQuoteResponse;

export type InvoiceKind = 'SUBSCRIPTION' | 'RAAS_STATEMENT';

/** `GET /billing/raas/summary?period=` (§4.4). */
export type RaasRateView = RaasRateViewResponse;
export type RaasSummary = RaasSummaryResponseDto;

export type RaasChargeKind = NonNullable<RaasChargeRowResponse['kind']>;
export type RaasChargeStatus = RaasChargeRowResponse['status'];
export type RaasDisputeBlock = RaasChargeRowResponse['disputeBlock'];
export type RaasDisputeReason =
  'NOT_TREATED' | 'DUPLICATE_PATIENT' | 'TEST_PATIENT' | 'IMPORTED_HISTORY' | 'OTHER';

/** One row of `GET /billing/raas/charges`. Names are masked by the server. */
export type RaasChargeRow = RaasChargeRowResponse;

export type RaasChargeFilter = 'all' | 'paid' | 'free' | 'disputed';

/** `GET /billing/refund` (§4.5). `blockers` are server copy, «[why]. [what to do].». */
export type RefundEligibility = RefundEligibilityResponse;

/** `POST /billing/refund` — the payout account travels encrypted (`pii-fields.ts`). */
export interface RefundRequestBody {
  iban: string;
  accountHolder: string;
  reason?: string;
}

/**
 * `GET /billing/treated-patients?months=` — treated patients per Jalali month,
 * OLDEST first; the current month is last with `complete: false`.
 */
export type TreatedPatientsMonth = TreatedPatientsMonthResponse;
export type { TreatedPatientsResponse };

export function fetchBillingState(signal?: AbortSignal): Promise<BillingState> {
  return billingSubscription(signal);
}

export function fetchPlans(signal?: AbortSignal) {
  return billingListPlans(signal);
}

export function fetchWallet(signal?: AbortSignal) {
  return billingWalletBalance(signal);
}

export function fetchInvoices(
  params: { page?: number; pageSize?: number; kind?: InvoiceKind } = {},
  signal?: AbortSignal,
): Promise<PagedResult<Invoice>> {
  // `kind` is billing-core's filter (C1); the generated caller forwards every
  // param to axios, so it reaches the server before the snapshot names it.
  return billingListInvoices(params, signal);
}

export function fetchRaasSummary(
  periodKey: string | null,
  signal?: AbortSignal,
): Promise<RaasSummary> {
  return apiGet<RaasSummary>(
    '/billing/raas/summary',
    periodKey ? { period: periodKey } : {},
    signal,
  );
}

export function fetchRaasCharges(
  params: { period: string; page: number; pageSize: number; filter: RaasChargeFilter },
  signal?: AbortSignal,
): Promise<PagedResult<RaasChargeRow>> {
  return apiGet<PagedResult<RaasChargeRow>>('/billing/raas/charges', params, signal);
}

export function fetchRefundEligibility(signal?: AbortSignal): Promise<RefundEligibility> {
  return apiGet<RefundEligibility>('/billing/refund', undefined, signal);
}

export function fetchTreatedPatients(
  months: number,
  signal?: AbortSignal,
): Promise<TreatedPatientsResponse> {
  return apiGet<TreatedPatientsResponse>('/billing/treated-patients', { months }, signal);
}

/**
 * `GET /v1/public/pricing` — the public catalogue, for the signup screen (no
 * clinic yet, so no `/billing/*` read is possible). Parsed by the shared
 * validator; anything malformed reads as «no catalogue», never as a price.
 */
export function fetchPublicPricing(signal?: AbortSignal): Promise<PublicPricing | null> {
  return apiGet<unknown>('/public/pricing', undefined, signal).then(parsePublicPricing);
}

export function fetchWalletTransactions(
  params: { page?: number; pageSize?: number } = {},
  signal?: AbortSignal,
): Promise<PagedResult<WalletTransaction>> {
  return billingWalletTransactions(params, signal);
}

export function fetchSubscriptionWallet(signal?: AbortSignal) {
  return billingSubscriptionWalletBalance(signal);
}

export function fetchSubscriptionWalletTransactions(
  params: { page?: number; pageSize?: number } = {},
  signal?: AbortSignal,
): Promise<PagedResult<WalletTransaction>> {
  return billingSubscriptionWalletTransactions(params, signal);
}

export const billingApi = {
  /**
   * What choosing a plan would do, before it is done: the prorated amount due
   * today, the credit for the unused days, and whether the change starts now
   * or at the period's end. `POST /billing/subscription` decides the same way,
   * so the confirm dialog can state the amount instead of promising to.
   * `termMonths` (1/6/12) prices a prepay term; omitted, the server quotes one
   * month.
   */
  quote: (
    planId: string,
    termMonths?: number,
    offer?: { offerCode?: string; offerGrantId?: string },
    signal?: AbortSignal,
  ): Promise<PlanChangeQuote> =>
    billingQuote(
      {
        planId,
        ...(termMonths ? { termMonths: termMonths as BillingQuoteTermMonths } : {}),
        ...(offer?.offerCode ? { offerCode: offer.offerCode } : {}),
        ...(offer?.offerGrantId ? { offerGrantId: offer.offerGrantId } : {}),
      },
      signal,
    ),
  subscribe: (input: {
    planId: string;
    termMonths?: number;
    returnPath?: string;
    offerCode?: string;
    offerGrantId?: string;
  }) =>
    billingSubscribe({
      planId: input.planId,
      ...(input.offerCode ? { offerCode: input.offerCode } : {}),
      ...(input.offerGrantId ? { offerGrantId: input.offerGrantId } : {}),
      ...(input.termMonths ? { termMonths: input.termMonths as SubscribeDtoTermMonths } : {}),
      ...(input.returnPath !== undefined ? { returnPath: input.returnPath } : {}),
    }),
  cancel: billingCancel,
  resume: billingResume,
  payInvoice: billingPayInvoice,
  invoicePdf: billingInvoicePdf,
  topUp: billingTopUp,
  topUpSubscriptionWallet: billingSubscriptionWalletTopUp,
  /** `POST /billing/subscription/scheduled/cancel` — drop a change waiting for the period's end. */
  cancelScheduled: (): Promise<unknown> => apiPost('/billing/subscription/scheduled/cancel'),
  /**
   * `POST /billing/subscription/trial-plan` — while TRIALING, try another plan
   * (FLAT or RaaS) for the rest of the trial. No invoice, no payment.
   */
  switchTrialPlan: (planKey: string): Promise<unknown> =>
    apiPost('/billing/subscription/trial-plan', { planKey }),
  /** `POST /billing/raas/charges/:id/dispute` — `note` is required for OTHER. */
  disputeCharge: (
    chargeId: string,
    body: { reasonCode: RaasDisputeReason; note?: string },
  ): Promise<unknown> =>
    apiPost(`/billing/raas/charges/${encodeURIComponent(chargeId)}/dispute`, body),
  /** `PUT /billing/raas/alert-budget` — `null` removes the alert. */
  setAlertBudget: (amountRials: number | null): Promise<unknown> =>
    apiPut('/billing/raas/alert-budget', { amountRials }),
  /** `POST /billing/refund` — the money-back request. */
  requestRefund: (body: RefundRequestBody): Promise<RefundEligibility> =>
    apiPost<RefundEligibility>('/billing/refund', body),
};

/* -------------------------------------------------------------------------- */
/* Sales offers                                                               */
/* -------------------------------------------------------------------------- */

export const offersApi = {
  list: (signal?: AbortSignal): Promise<ClinicOffersResponse> => offersList(signal),
  enterCode: (code: string): Promise<ClinicOfferResponse> => offersEnterCode({ code }),
  seen: (grantId: string, via: 'panel' | 'modal' = 'panel'): Promise<ClinicOfferResponse> =>
    offersMarkSeen(grantId, { via }),
  dismiss: (grantId: string): Promise<void> => offersDismiss(grantId),
  claim: (grantId: string): Promise<ClinicOfferClaimResponse> => offersClaim(grantId),
};

/* -------------------------------------------------------------------------- */
/* Onboarding — starter kits                                                  */
/* -------------------------------------------------------------------------- */

export function fetchStarterKits(signal?: AbortSignal): Promise<StarterKitSummary[]> {
  return starterKitsList(signal).then((result) => result.items);
}

/**
 * Everything above is a plain function rather than a re-export alias on
 * purpose: a named re-export is a live binding into the generated module, and a
 * test that wants to stub one screen's read ends up reaching into orval's
 * output. One local function per read keeps the seam here.
 */

export const starterKitApi = {
  preview: (key: string, signal?: AbortSignal): Promise<StarterKitPreview> =>
    starterKitsPreview(key, signal),
  apply: (key: ApplyStarterKitDtoKey): Promise<StarterKitApplyResult> => starterKitsApply({ key }),
};

/* -------------------------------------------------------------------------- */
/* Onboarding — patient import                                                */
/* -------------------------------------------------------------------------- */

export function fetchImportFormat(signal?: AbortSignal) {
  return patientImportFormat(signal);
}

export const patientImportApi = {
  /** Dry run: parses, validates and reports — writes nothing. */
  preview: (file: File): Promise<PatientImportResult> => patientImportPreview({ file }),
  /** The real thing. Same parser, same report, rows actually created. */
  commit: (file: File): Promise<PatientImportResult> => patientImportCommit({ file }),
  /**
   * The template download.
   *
   * Not the generated caller: orval types it `Promise<void>` because the
   * response is a binary body with a `Content-Disposition`, and the shared
   * mutator unwraps a `{ data }` envelope that a spreadsheet does not have.
   * The raw axios instance with `responseType: 'blob'` is the honest way to
   * ask for a file, and it still carries the bearer token and the refresh
   * queue because it is the same instance.
   */
  async downloadTemplate(format: 'xlsx' | 'csv'): Promise<{ blob: Blob; filename: string }> {
    const response = await requestBlob(http, '/onboarding/patients/import/template', {
      params: { format },
    });
    const disposition = String(response.headers['content-disposition'] ?? '');
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match?.[1] ? decodeURIComponent(match[1]) : `bookora-patients.${format}`;
    return { blob: response.data, filename };
  },
};

/* -------------------------------------------------------------------------- */
/* Two-factor                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Enrolment is a three-step handshake and the order matters.
 *
 *  1. `enrol()` mints a secret, stores it encrypted and returns the
 *     `otpauth://` URI. **The second factor is not active yet** — closing the
 *     tab here leaves an account nobody is locked out of.
 *  2. `verify(code)` proves the authenticator holds the same secret, turns 2FA
 *     on, and returns the recovery codes.
 *  3. Those codes are shown **once**. The server keeps only their hashes, so
 *     there is no endpoint that returns them again and this panel offers no
 *     affordance that pretends otherwise. `regenerate` replaces the whole set
 *     and invalidates the old one.
 */
export const twoFactorApi = {
  enrol: () => twoFactorEnrol(),
  verify: (code: string) => twoFactorVerify({ code }),
  disable: (code: string) => twoFactorDisable({ code }),
  regenerate: (code: string) => twoFactorRegenerateRecoveryCodes({ code }),
};
